import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import {
  DevicePermission,
  DevicePermissionState,
  GpsSignalStatus,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useState } from 'react';

import GpsStatusIcon, { GpsStatusText, IdleIcon } from '~components/Common/GpsStatusIcon';
import { DeviceStatusRead } from '~hooks/useDeviceStatus/models';
import { dateFormat } from '~utils/dateTime';
import { hexToRgba } from '~utils/utilFunctions';

interface StatusBannerProps {
  color: string;
  icon: React.ReactNode;
  subtitle: string;
  title: string;
}

const StatusBanner = ({ color, icon, subtitle, title }: StatusBannerProps) => (
  <Box
    sx={{
      alignItems: 'center',
      backgroundColor: hexToRgba(color, 0.15),
      borderRadius: 1,
      display: 'flex',
      gap: 0.75,
      marginTop: 1,
      paddingBlock: 0.75,
      paddingInline: 1,
    }}
  >
    {icon}
    <Typography
      sx={{
        color,
        fontSize: '13px',
        fontWeight: 600,
      }}
    >
      {title}
      {' • '}
      {subtitle}
    </Typography>
  </Box>
);

interface LocationDataProps {
  deviceStatus?: DeviceStatusRead | null;
  isLoading: boolean;
  isError: boolean;
  refetchDeviceStatus: () => void;
}

export const LocationData = ({
  deviceStatus,
  isLoading,
  isError,
  refetchDeviceStatus,
}: LocationDataProps) => {
  const theme = useTheme();
  useState<boolean>(false);

  const PermissionState: React.FC<{ state?: DevicePermissionState }> = ({ state }) => {
    const isGranted = state === DevicePermissionState.GRANTED;
    const formatedState = state?.replace(/_/g, ' ').toLowerCase();
    return (
      <Typography
        color={
          isGranted ? theme.brandV2.colors.treadGreen : theme.brandV2.colors.treadRed
        }
        sx={{
          fontSize: '12px',
          fontWeight: 400,
        }}
      >
        {!formatedState
          ? '-'
          : formatedState?.charAt(0).toUpperCase() +
            formatedState?.slice(1).toLowerCase()}
      </Typography>
    );
  };

  const preciseLocationState = deviceStatus?.userDevicePermissions.find(
    (p) => p.devicePermission === DevicePermission.LOCATION_PRECISE,
  )?.devicePermissionState;
  const driverIsOnApproximateLocation =
    preciseLocationState === DevicePermissionState.DENIED;
  const gpsIsNotReportingOnline =
    deviceStatus?.gpsStatus?.status !== GpsSignalStatus.ONLINE;
  // Approximate location makes pings inaccurate enough that the backend drops them, so a
  // driver who is also not reporting online is misconfigured rather than merely out of touch.
  const hasLocationAccuracyError =
    driverIsOnApproximateLocation && gpsIsNotReportingOnline;

  return (
    <Box
      sx={{
        padding: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: '240px',
      }}
    >
      {isLoading ? (
        <CircularProgress size={24} sx={{ alignSelf: 'center', marginBlock: 2 }} />
      ) : isError ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            padding: 2,
          }}
        >
          <Typography
            color={theme.brandV2.colors.treadGray1}
            sx={{
              fontSize: '14px',
              fontWeight: 400,
            }}
          >
            {t('live_map.location_data.error_getting_location_data')}
          </Typography>
          <Button
            variant="contained"
            size="medium"
            color="secondary"
            onClick={() => {
              refetchDeviceStatus();
            }}
          >
            {t('actions.try_again')}
          </Button>
        </Box>
      ) : (
        <>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
            }}
          >
            {t('live_map.location_data.location_sharing')}
          </Typography>
          {deviceStatus?.gpsStatus?.status === GpsSignalStatus.ONLINE &&
            deviceStatus?.gpsStatus?.idle && (
              <StatusBanner
                color={theme.brandV2.colors.treadOrangeDark}
                icon={<IdleIcon size={18} />}
                subtitle={t('live_map.location_data.idle_subtitle')}
                title={t('live_map.location_data.idle_title')}
              />
            )}
          {hasLocationAccuracyError && (
            <StatusBanner
              color={theme.brandV2.colors.treadRed}
              icon={
                <ErrorOutline
                  data-test-id="location-accuracy-error-icon"
                  sx={{
                    color: theme.brandV2.colors.treadRed,
                    fontSize: '18px',
                  }}
                />
              }
              subtitle={t('live_map.location_data.location_accuracy_error_subtitle')}
              title={t('live_map.location_data.location_accuracy_error_title')}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              {t('live_map.location_data.gps_status')}
            </Typography>

            {deviceStatus?.gpsStatus && (
              <Box
                sx={{
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.5,
                  marginBottom: 0.5,
                }}
              >
                <GpsStatusIcon
                  gpsStatus={deviceStatus.gpsStatus}
                  hasLocationAccuracyError={hasLocationAccuracyError}
                  showIdleBadge={false}
                />

                <GpsStatusText
                  gpsStatus={deviceStatus.gpsStatus}
                  hasLocationAccuracyError={hasLocationAccuracyError}
                  sx={{ fontSize: '12px', fontWeight: 500 }}
                />
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              marginTop: 0.5,
              marginBottom: 1,
            }}
          >
            <Typography
              color={theme.brandV2.colors.treadGray2}
              sx={{
                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              {t('live_map.location_data.last_gps_ping')}
            </Typography>

            <Typography
              color={theme.brandV2.colors.treadGray2}
              align="right"
              sx={{
                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              {deviceStatus?.gpsStatus.last_ping_at
                ? dateFormat(
                    dayjs(deviceStatus.gpsStatus.last_ping_at),
                    'MMM DD @ hh:mm:ss A',
                  )
                : '-'}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {t('live_map.location_data.permissions')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              marginTop: 0.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                color={theme.brandV2.colors.treadGray2}
                sx={{
                  fontWeight: 400,
                  fontSize: '12px',
                }}
              >
                {t('live_map.location_data.location_while_using')}
              </Typography>

              <PermissionState
                state={
                  deviceStatus?.userDevicePermissions.find(
                    (p) => p.devicePermission === DevicePermission.LOCATION_WHILE_USING,
                  )?.devicePermissionState
                }
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                color={theme.brandV2.colors.treadGray2}
                sx={{
                  fontWeight: 400,
                  fontSize: '12px',
                }}
              >
                {t('live_map.location_data.location_always')}
              </Typography>

              <PermissionState
                state={
                  deviceStatus?.userDevicePermissions.find(
                    (p) => p.devicePermission === DevicePermission.LOCATION_ALWAYS,
                  )?.devicePermissionState
                }
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  color: theme.brandV2.colors.treadGray2,
                  fontWeight: 400,
                  fontSize: '12px',
                }}
              >
                {t('live_map.location_data.location_precise')}
              </Typography>

              <PermissionState state={preciseLocationState} />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                color={theme.brandV2.colors.treadGray2}
                sx={{
                  fontWeight: 400,
                  fontSize: '12px',
                }}
              >
                {t('live_map.location_data.motion_activity')}
              </Typography>

              <PermissionState
                state={
                  deviceStatus?.userDevicePermissions.find(
                    (p) => p.devicePermission === DevicePermission.MOTION_AND_FITNESS,
                  )?.devicePermissionState
                }
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                color={theme.brandV2.colors.treadGray2}
                sx={{
                  fontWeight: 400,
                  fontSize: '12px',
                }}
              >
                {t('live_map.location_data.physical_activity')}
              </Typography>
              <PermissionState
                state={
                  deviceStatus?.userDevicePermissions.find(
                    (p) => p.devicePermission === DevicePermission.PHYSICAL_ACTIVITY,
                  )?.devicePermissionState
                }
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
